<template>
    <div
        v-if="show"
        class="fixed inset-0 z-50 flex items-center justify-center"
    >
        <div
            class="absolute inset-0 bg-slate-900/50"
            @click="$emit('update:show', false)"
        ></div>
        <div
            class="relative bg-white dark:bg-slate-800 rounded-lg shadow-lg w-full max-w-sm md:max-w-lg mx-4 p-6"
        >
            <div class="flex items-center justify-between mb-6">
                <h3 class="text-xl font-medium text-slate-900 dark:text-slate-100">
                    Edit payment method
                </h3>
                <button
                    class="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-700 transition-colors"
                    @click="$emit('update:show', false)"
                >
                    <Icon icon="close" class="w-5 h-5 text-slate-500 dark:text-slate-400" />
                </button>
            </div>

            <div class="mb-4">
                <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5"
                >
                    Card number
                </label>
                <div
                    class="flex items-center justify-between px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded bg-slate-50 dark:bg-slate-900/40 text-sm text-slate-500 dark:text-slate-400"
                >
                    <span>
                        &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull;
                        &bull;&bull;&bull;&bull;
                        {{ card?.card?.last4 }}
                    </span>
                    <span
                        class="text-xs font-semibold uppercase text-slate-600 dark:text-slate-300"
                    >
                        {{ cardBrandLabel(card?.card?.brand) }}
                    </span>
                </div>
            </div>

            <div class="flex gap-4 mb-4">
                <div class="flex-1">
                    <label
                        class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5"
                    >
                        Expiration date
                    </label>
                    <input
                        v-model="editExpDate"
                        type="text"
                        placeholder="MM/YY"
                        maxlength="5"
                        class="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-orange focus:border-orange"
                        @input="formatExpDate"
                    />
                </div>
                <div class="flex-1">
                    <label
                        class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5"
                    >
                        CVC
                    </label>
                    <input
                        type="text"
                        placeholder="123"
                        disabled
                        class="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded text-sm bg-slate-50 dark:bg-slate-900/40 text-slate-400"
                    />
                </div>
            </div>

            <div class="mb-6">
                <label
                    class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5"
                >
                    Cardholder name
                </label>
                <input
                    v-model="editCardholderName"
                    type="text"
                    placeholder="Full name"
                    class="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-orange focus:border-orange"
                />
            </div>

            <div v-if="editCardError" class="text-sm text-red-500 mb-4">
                {{ editCardError }}
            </div>

            <div class="flex justify-end gap-3">
                <button
                    class="px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-slate-100 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors"
                    @click="$emit('update:show', false)"
                >
                    Cancel
                </button>
                <button
                    class="px-4 py-2.5 bg-slate-900 text-sm font-medium text-white rounded shadow-sm hover:bg-slate-800 transition-colors disabled:opacity-50"
                    :disabled="editCardProcessing"
                    @click="handleEditCard"
                >
                    {{ editCardProcessing ? 'Saving...' : 'Save changes' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    card: {
        type: Object,
        default: null,
    },
});

const emit = defineEmits(['update:show', 'card-updated']);

const editExpDate = ref('');
const editCardholderName = ref('');
const editCardError = ref('');
const editCardProcessing = ref(false);

const cardBrandLabel = (brand) => {
    if (!brand) return 'Card';
    const labels = {
        visa: 'Visa',
        mastercard: 'Mastercard',
        amex: 'Amex',
        discover: 'Discover',
        diners: 'Diners',
        jcb: 'JCB',
        unionpay: 'UnionPay',
    };
    return (
        labels[brand.toLowerCase()] ||
        brand.charAt(0).toUpperCase() + brand.slice(1)
    );
};

const formatExpDate = (e) => {
    let val = e.target.value.replace(/\D/g, '');
    if (val.length > 2) {
        val = val.slice(0, 2) + '/' + val.slice(2, 4);
    }
    editExpDate.value = val;
};

const handleEditCard = async () => {
    if (!props.card) return;

    const parts = editExpDate.value.split('/');
    if (
        parts.length !== 2 ||
        !parts[0] ||
        !parts[1] ||
        parts[1].length !== 2
    ) {
        editCardError.value = 'Please enter a valid expiration date (MM/YY).';
        return;
    }

    const expMonth = parseInt(parts[0], 10);
    const expYear = parseInt('20' + parts[1], 10);

    if (expMonth < 1 || expMonth > 12) {
        editCardError.value = 'Invalid month.';
        return;
    }

    editCardProcessing.value = true;
    editCardError.value = '';

    try {
        await ApiService.apiCall(
            `/travel-plus/payment-method/${props.card.id}`,
            'put',
            {
                exp_month: expMonth,
                exp_year: expYear,
                name: editCardholderName.value,
            }
        );

        emit('card-updated', {
            id: props.card.id,
            exp_month: expMonth,
            exp_year: expYear,
            name: editCardholderName.value,
        });
        emit('update:show', false);
    } catch (e) {
        editCardError.value =
            e?.response?.data?.message ||
            'Something went wrong. Please try again.';
    } finally {
        editCardProcessing.value = false;
    }
};

watch(
    () => props.show,
    (val) => {
        if (val && props.card) {
            const month = String(props.card.card?.exp_month).padStart(2, '0');
            const year = String(props.card.card?.exp_year).slice(-2);
            editExpDate.value = `${month}/${year}`;
            editCardholderName.value = props.card.billing_details?.name || '';
            editCardError.value = '';
        }
    }
);
</script>
